"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"

export interface DataContextValue {
  isEmptyMode: boolean
  setIsEmptyMode: (mode: boolean) => void
  currency: string
  setCurrency: (currency: string) => void
}

const DataContext = createContext<DataContextValue | null>(null)

export function DataProvider({ children }: { children: ReactNode }) {
  const [isEmptyMode, setIsEmptyMode] = useState(false)
  const [currency, setCurrencyState] = useState("EUR")

  useEffect(() => {
    const savedMode = localStorage.getItem("zentjes_dev_mode")
    const savedCurrency = localStorage.getItem("zentjes_currency")

    if (savedMode === "empty") {
      setIsEmptyMode(true)
    }

    if (savedCurrency) {
      setCurrencyState(savedCurrency)
    }

    const prefs = localStorage.getItem("zentjes_preferences")
    if (prefs) {
      try {
        const parsed = JSON.parse(prefs)
        if (parsed.currency) setCurrencyState(parsed.currency)
      } catch {}
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("zentjes_dev_mode", isEmptyMode ? "empty" : "populated")
  }, [isEmptyMode])

  function setCurrency(newCurrency: string): void {
    setCurrencyState(newCurrency)
    localStorage.setItem("zentjes_currency", newCurrency)
  }

  return (
    <DataContext.Provider
      value={{
        isEmptyMode,
        setIsEmptyMode,
        currency,
        setCurrency,
      }}
    >
      {children}
    </DataContext.Provider>
  )
}

export function useData(): DataContextValue {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error("useData must be used within DataProvider")
  }
  return context
}
